import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Paper,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Refresh as RefreshIcon, Code as CodeIcon, Visibility as UIIcon } from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { DynamicFormBuilder } from '../../../base/SchemaFormBuilder';
import { BaseButton } from '../../../base';
import ConfigManagementService, { ConfigScheme } from '../../../../../services/ConfigManagementService';

const COLORS = {
  ACCENT: '#005e5d',
  BORDER: '#E6E7EB',
  TEXT_PRIMARY: '#333333',
  TEXT_SECONDARY: '#666666',
  TEXT_TERTIARY: '#999999',
};

export interface ConfigurationTabProps {
  schemes: ConfigScheme[];
  selectedConfigKey: string | null;
  configData: Record<string, any> | null;
  loading: boolean;
  saving?: boolean;
  
  // Event handlers
  onSelectConfig: (key: string) => void;
  onSaveConfig?: (data: Record<string, any>) => void;
  onRefresh: () => void;
}

/**
 * Configuration management tab
 * Sidebar with config schemas + main area with dynamic form
 * Form renders directly without modal - click schema to edit
 */
export const ConfigurationTab: React.FC<ConfigurationTabProps> = ({
  schemes,
  selectedConfigKey,
  configData,
  loading,
  saving = false,
  onSelectConfig,
  onSaveConfig,
  onRefresh,
}) => {
  const [formData, setFormData] = useState<Record<string, any>>(configData || {});
  const [selectedSchemeDefinition, setSelectedSchemeDefinition] = useState<Record<string, any> | null>(null);
  const [fetchingSchema, setFetchingSchema] = useState(false);
  const [viewMode, setViewMode] = useState<'ui' | 'json'>('ui');
  const formRef = useRef<HTMLFormElement>(null);
  const isSavingRef = useRef(false);

  const configSchemes = schemes.filter((s) => s.type === 'config');
  const selectedScheme = configSchemes.find((s) => s.key === selectedConfigKey);

  // Fetch full schema definition when selectedConfigKey changes
  useEffect(() => {
    if (!selectedConfigKey) {
      setSelectedSchemeDefinition(null);
      return;
    }

    const fetchSchemaDefinition = async () => {
      try {
        setFetchingSchema(true);
        const schemaData = await ConfigManagementService.getSchemeByKey(selectedConfigKey);
        const rawSchema = schemaData.schemaDefinition || {};
        
        // Normalize schema structure to match DynamicFormBuilder expectations
        let normalizedSchema = rawSchema;
        
        if (!rawSchema.properties) {
          const systemKeys = ['$schema', 'type', 'title', 'description', 'required', 'additionalProperties'];
          
          // Case 1: Single field definition at root level (has x-control directly)
          // e.g., { "title": "header-navigate", "x-control": {...} }
          if (rawSchema['x-control'] && Object.keys(rawSchema).length <= 4) {
            const fieldName = selectedConfigKey; // Use config key as field name
            normalizedSchema = {
              properties: {
                [fieldName]: rawSchema,
              },
            };
          } else {
            // Case 2: Multiple flat properties (each property has x-control)
            const hasDirectProps = Object.keys(rawSchema).some(
              key => !systemKeys.includes(key) && rawSchema[key] && rawSchema[key]['x-control']
            );
            
            if (hasDirectProps) {
              normalizedSchema = {
                ...rawSchema,
                properties: Object.keys(rawSchema).reduce((props, key) => {
                  if (!systemKeys.includes(key) && rawSchema[key]['x-control']) {
                    props[key] = rawSchema[key];
                  }
                  return props;
                }, {} as Record<string, any>),
              };
            }
          }
        }
        
        setSelectedSchemeDefinition(normalizedSchema);
      } catch (err) {
        console.error('Failed to fetch schema definition:', err);
        setSelectedSchemeDefinition(null);
      } finally {
        setFetchingSchema(false);
      }
    };

    fetchSchemaDefinition();
  }, [selectedConfigKey]);

  // Update form when configData changes
  useEffect(() => {
    setFormData(configData || {});
  }, [configData]);

  const handleFormSubmit = (data: Record<string, any>) => {
    setFormData(data);
    
    // If Save button was clicked, also call onSaveConfig
    if (isSavingRef.current) {
      isSavingRef.current = false;
      if (onSaveConfig) {
        onSaveConfig(data);
      }
    }
  };

  const handleSave = async () => {
    // Mark that we're saving and submit the form
    isSavingRef.current = true;
    if (formRef.current) {
      formRef.current.requestSubmit();
    } else {
      // Fallback if form ref not available
      if (onSaveConfig) {
        onSaveConfig(formData);
      }
    }
  };

  return (
    <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 2, pt: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Configuration Management
        </Typography>
        <Tooltip title="Refresh data">
          <IconButton onClick={onRefresh} disabled={loading} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {configSchemes.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No configuration schemas found. Create a config type schema first.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, px: 2, pb: 2, flex: 1, overflow: 'hidden' }}>
          {/* SIDEBAR - Config List */}
          <Paper
            elevation={0}
            sx={{
              width: '250px',
              border: `1px solid ${COLORS.BORDER}`,
              borderRadius: '14px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#fafafa',
            }}
          >
            <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderBottom: `1px solid ${COLORS.BORDER}` }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.TEXT_SECONDARY }}>
                CONFIGURATIONS
              </Typography>
            </Box>

            <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
              {configSchemes.map((scheme, index) => (
                <Box key={scheme.key}>
                  <ListItemButton
                    selected={selectedConfigKey === scheme.key}
                    onClick={() => onSelectConfig(scheme.key)}
                    disabled={loading}
                    sx={{
                      '&.Mui-selected': {
                        bgcolor: COLORS.BORDER,
                        borderLeft: `4px solid ${COLORS.ACCENT}`,
                        '&:hover': { bgcolor: COLORS.BORDER },
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            fontSize: '13px',
                            fontWeight: selectedConfigKey === scheme.key ? 600 : 500,
                            color: selectedConfigKey === scheme.key ? COLORS.ACCENT : COLORS.TEXT_PRIMARY,
                          }}
                        >
                          {scheme.label || scheme.key}
                        </Typography>
                      }
                      secondary={
                        scheme.description && (
                          <Typography variant="caption" sx={{ fontSize: '11px', color: COLORS.TEXT_TERTIARY }}>
                            {scheme.description}
                          </Typography>
                        )
                      }
                    />
                  </ListItemButton>
                  {index < configSchemes.length - 1 && <Divider sx={{ my: 0 }} />}
                </Box>
              ))}
            </List>
          </Paper>

          {/* MAIN CONTENT - Config Form */}
          {selectedScheme ? (
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '14px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${COLORS.BORDER}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#fafafa',
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedScheme.label || selectedScheme.key}
                  </Typography>
                  {selectedScheme.description && (
                    <Typography variant="caption" color="textSecondary">
                      {selectedScheme.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* UI/JSON Toggle */}
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(e, newMode) => {
                      if (newMode !== null) {
                        setViewMode(newMode);
                      }
                    }}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        border: `1px solid ${COLORS.BORDER}`,
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                        textTransform: 'none',
                        padding: '6px 12px',
                        '&.Mui-selected': {
                          bgcolor: COLORS.ACCENT,
                          color: '#fff',
                          border: `1px solid ${COLORS.ACCENT}`,
                          '&:hover': {
                            bgcolor: COLORS.ACCENT,
                          },
                        },
                        '&:hover': {
                          bgcolor: '#f5f5f5',
                        },
                      },
                    }}
                  >
                    <ToggleButton value="ui" aria-label="UI view">
                      <UIIcon sx={{ fontSize: '16px', mr: 0.5 }} />
                      UI
                    </ToggleButton>
                    <ToggleButton value="json" aria-label="JSON view">
                      <CodeIcon sx={{ fontSize: '16px', mr: 0.5 }} />
                      JSON
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Tooltip title="Refresh">
                    <IconButton size="small" onClick={onRefresh} disabled={loading}>
                      <RefreshIcon sx={{ fontSize: '18px' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Form Content */}
              <Box
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {fetchingSchema ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : viewMode === 'ui' ? (
                  // UI View
                  selectedSchemeDefinition && selectedSchemeDefinition.properties && Object.keys(selectedSchemeDefinition.properties).length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
                      <DynamicFormBuilder
                        schema={selectedSchemeDefinition}
                        initialData={formData}
                        onSubmit={handleFormSubmit}
                        loading={saving}
                        hideSubmitButton={true}
                        formRef={formRef}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="textSecondary">
                        No schema fields defined. Edit the schema first.
                      </Typography>
                    </Box>
                  )
                ) : (
                  // JSON View
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '400px' }}>
                    <Editor
                      height="100%"
                      defaultLanguage="json"
                      value={JSON.stringify(formData, null, 2)}
                      theme="light"
                      options={{
                        minimap: { enabled: false },
                        readOnly: false,
                        scrollBeyondLastLine: false,
                        fontSize: 13,
                        lineNumbers: 'on',
                        formatOnPaste: true,
                        formatOnType: true,
                      }}
                      onChange={(value) => {
                        if (value) {
                          try {
                            const parsed = JSON.parse(value);
                            setFormData(parsed);
                          } catch (e) {
                            // Invalid JSON - don't update state
                          }
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>

              {/* Footer - Save Button */}
              <Box
                sx={{
                  p: 2,
                  borderTop: `1px solid ${COLORS.BORDER}`,
                  bgcolor: '#f5f5f5',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                }}
              >
                <BaseButton
                  variant="contained"
                  colorVariant="primary"
                  onClick={handleSave}
                  disabled={saving || loading}
                  isLoading={saving}
                  loadingLabel="Saving..."
                >
                  Save Configuration
                </BaseButton>
              </Box>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography color="textSecondary">
                {loading ? <CircularProgress size={24} /> : 'Select a configuration schema'}
              </Typography>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ConfigurationTab;
